import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { HERO_SLIDES, HERO_VIDEO } from "../mock/mock";
import { useContent } from "../context/ContentContext";

export default function HeroVideo() {
  const { heroSlides = HERO_SLIDES, heroVideo = HERO_VIDEO } = useContent();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % heroSlides.length), 6500);
    return () => clearInterval(t);
  }, [heroSlides.length]);

  const slide = heroSlides[index];

  return (
    <section className="relative w-full h-[100vh] min-h-[640px] overflow-hidden">
      {/* Video background */}
      <video
        className="absolute inset-0 w-full h-full object-cover"
        src={heroVideo.sources[0]}
        poster={heroVideo.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
      />
      {/* Overlays */}
      <div className="absolute inset-0 bg-[#0a0a0a]/55" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/30 via-[#0a0a0a]/45 to-[#0a0a0a]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/70 via-transparent to-transparent" />
      <div
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #B87333 1px, transparent 0)",
          backgroundSize: "36px 36px"
        }}
      />

      {/* Content */}
      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-center pt-20">
        <div className="max-w-4xl">
          <div key={index} className="anim-fade-up">
            <div className="flex items-center gap-3 mb-6">
              <span className="divider-line" />
              <span className="text-[11px] tracking-[0.4em] text-[#B87333] font-sans-body">
                {slide.subtitle}
              </span>
            </div>
            <h1 className="font-serif-display text-[#f5ede1] text-5xl md:text-7xl lg:text-[84px] leading-[1.05] font-light">
              {slide.title}
            </h1>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to={slide.cta.path}
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[#B87333] text-[#0a0a0a] text-[11px] tracking-[0.3em] font-semibold hover:bg-[#D4A574] transition-colors duration-300"
              >
                {slide.cta.label.toUpperCase()}
                <span className="w-4 h-px bg-[#0a0a0a] group-hover:w-8 transition-all" />
              </Link>
              <Link
                to="/hakkimizda"
                className="inline-flex items-center gap-3 px-8 py-4 border border-[#B87333]/50 text-[#f5ede1] text-[11px] tracking-[0.3em] font-semibold hover:border-[#B87333] hover:text-[#B87333] transition-colors duration-300"
              >
                HAKKIMIZDA
              </Link>
            </div>
          </div>
        </div>

        {/* Giant outline kicker */}
        <div className="pointer-events-none absolute right-[-2%] top-[12%] hidden md:block select-none">
          <span
            key={`kick-${index}`}
            className="font-serif-display text-outline-cream text-[180px] lg:text-[260px] leading-none tracking-tighter anim-fade-up block"
          >
            {slide.kicker}
          </span>
        </div>

        {/* Slide dots */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex gap-3">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Slide ${i + 1}`}
              className={`h-[2px] transition-all duration-500 ${
                i === index ? "w-12 bg-[#B87333]" : "w-6 bg-[#f5ede1]/30 hover:bg-[#f5ede1]/60"
              }`}
            />
          ))}
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-12 right-8 hidden md:flex flex-col items-center gap-2 text-[#f5ede1]/60">
          <span className="text-[10px] tracking-[0.3em] writing-mode-vertical">SCROLL</span>
          <ChevronDown className="w-4 h-4 animate-bounce" />
        </div>
      </div>
    </section>
  );
}
